import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { FlexCol, FlexRow } from "../../../../../utils/flexLayouts";
import { useFiltersContext } from "src/contexts/FilterContext";
import FavoriteButton from "../FavoriteButton";
import _ from "lodash";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getNavigation } from "src/components/Sidebar";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { NavItemType } from "src/layouts/DashboardLayout";
import useListFilters from "src/hooks/useListFilters";
import { Keys, RestKeys } from "../../type";
import { dependenciesListName, equipmentsListName, favoriteFiltersListName, linesListName } from "src/api/config";
import { getItems } from "src/modules/spItems/actions";
import useGetBreadCrumbs from "src/hooks/useGetBreadCrumbs";
import { useFoldersContext } from "src/contexts/FolderContext";
import {
  selectAddStatus,
  selectDeleteStatus,
  selectGetStatus,
  selectItems,
  selectUpdateStatus,
} from "src/modules/spItems/selectors";
import { FilterDateItem, FilterEquipmentsItem, FilterItem, FilterUserItem } from "./FilterItems";
import { useGetOneSelect } from "src/hooks/redux-selectors";
import Loading from "src/components/Loading";
import { ChevronIcon } from "src/pages/Reports/shared/icons";

function ResetFilters() {
  const { resetFilters } = useFiltersContext();
  const { clearFolders } = useFoldersContext();

  function clearAll() {
    resetFilters();
    clearFolders();
  }

  return (
    <>
      <FlexRow
        className="flex-nowrap gap-x-2 items-center p-5 border-b cursor-pointer group"
        onClick={() => clearAll()}
      >
        <FlexCol className="group-hover:underline">Сбросить все фильтры</FlexCol>
        <FlexCol>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_381_6476)">
              <path
                d="M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z"
                fill="#637381"
              />
            </g>
            <defs>
              <clipPath id="clip0_381_6476">
                <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
              </clipPath>
            </defs>
          </svg>
        </FlexCol>
      </FlexRow>
    </>
  );
}

type BreadcrumbProps = {
  isFavorite: boolean;
  currentFavoriteId: string | null;
  bundlesModalOpened: boolean;
  setBundlesModalOpened: Dispatch<SetStateAction<boolean>>;
  onSaveFilter: () => void;
  onDeleteFilter: () => void;
  onUpdateFilter: () => void;
};

export function DocumentPageFilterBreadcrumbLayout({
  isFavorite,
  currentFavoriteId,
  bundlesModalOpened,
  setBundlesModalOpened,
  onSaveFilter,
  onDeleteFilter,
  onUpdateFilter,
}: BreadcrumbProps) {
  const { id: queryId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { filters } = useFiltersContext();

  const favoriteFiltersData = useAppSelector((state) => selectItems(state, favoriteFiltersListName));
  const favoriteFiltersGetStatus = useAppSelector((state) => selectGetStatus(state, favoriteFiltersListName));
  const favoriteFiltersUpdatedStatus = useAppSelector((state) => selectUpdateStatus(state, favoriteFiltersListName));
  const favoriteFiltersDeletedStatus = useAppSelector((state) => selectDeleteStatus(state, favoriteFiltersListName));
  const favoriteFiltersAddedStatus = useAppSelector((state) => selectAddStatus(state, favoriteFiltersListName));
  const { breadcrumbs, length: breadcrumbsLength } = useGetBreadCrumbs(filters);

  const navItem: NavItemType | undefined = useMemo(
    () => getNavigation(queryId).find((n) => n.url === pathname),
    [queryId, pathname],
  );

  const hasNonEmptyArray = useMemo(
    () =>
      _.some(filters, (value) => _.isArray(value) && !_.isEmpty(value)) ||
      filters.dateForRevision.required ||
      !!filters.dateForRevision.date.start,
    [JSON.stringify(filters)],
  );

  const loadingStatus =
    favoriteFiltersUpdatedStatus === "LOADING" ||
    favoriteFiltersDeletedStatus === "LOADING" ||
    favoriteFiltersAddedStatus === "LOADING" ||
    favoriteFiltersGetStatus === "LOADING";

  const handleToggleFavorite = () => {
    if (isFavorite) {
      onDeleteFilter();
    } else {
      onSaveFilter();
    }
    setBundlesModalOpened(false);
  };

  if (!navItem) {
    navigate("/");
    return null;
  }

  return (
    <nav className="flex justify-between w-full" aria-label="Breadcrumb">
      <FlexCol className="grow">
        <ol className="inline-flex items-center">
          <li className="inline-flex items-center" aria-current="page">
            <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
              {navItem.icon ?? null}
              <span>{navItem.name}</span>
            </div>
            <span className="px-3 text-body-color">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                  fill={"#637381"}
                />
              </svg>
            </span>
          </li>
          {!breadcrumbsLength && (
            <li className="inline-flex items-center" aria-current="page">
              <div className="inline-flex items-center space-x-2 text-base font-medium">
                <span>Фильтры не выбраны</span>
              </div>
            </li>
          )}
          {isFavorite && currentFavoriteId ? (
            <li
              className="flex items-center mr-1 text-base font-medium whitespace-nowrap cursor-pointer"
              aria-current="step"
              onClick={onSaveFilter}
            >
              <CurrentFavoriteTitle id={Number(currentFavoriteId)} onUpdateFilter={onUpdateFilter} />
            </li>
          ) : null}
          <li className="flex items-center grow" aria-current="page">
            <div className="flex items-center w-full text-base font-medium">{breadcrumbs}</div>
          </li>
        </ol>
      </FlexCol>
      <FlexCol className="flex-row items-center gap-[10px]">
        {hasNonEmptyArray && (
          <FavoriteButton
            isFavorite={{ state: isFavorite, id: null }}
            onToggle={handleToggleFavorite}
            disabled={loadingStatus}
          />
        )}
        {loadingStatus ? (
          <Loading />
        ) : favoriteFiltersData.length > 0 ? (
          <button onClick={() => setBundlesModalOpened((prev) => !prev)}>
            <ChevronIcon className="w-5 h-3 duration-300 rotate-180 text-dark6 hover:text-darkblue" />
          </button>
        ) : null}
      </FlexCol>
    </nav>
  );
}

function CurrentFavoriteTitle({ id, onUpdateFilter }: { id: number; onUpdateFilter: () => void }) {
  const filter = useGetOneSelect(favoriteFiltersListName, id);

  const handleUpdateFilter = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    onUpdateFilter();
  };

  return (
    <>
      <span className="underline hover:no-underline" onClick={handleUpdateFilter}>
        {filter?.Title}
      </span>
      :
    </>
  );
}

function DocumentFilters() {
  const dispatch = useAppDispatch();
  const { filters } = useFiltersContext();
  const status = useAppSelector((state) => selectGetStatus(state, equipmentsListName));

  const hasNonEmptyArray = useMemo(
    () =>
      _.some(
        filters,
        (value) =>
          (_.isArray(value) && !_.isEmpty(value)) ||
          filters.dateForRevision.required ||
          !!filters.dateForRevision.date.start,
      ),
    [JSON.stringify(filters)],
  );

  useEffect(() => {
    if (status === "IDLE") {
      dispatch(
        getItems({
          listName: equipmentsListName,
        }),
      );
    }
  }, [dispatch, status]);

  return (
    <FlexCol className="grow">
      {hasNonEmptyArray && <ResetFilters />}
      <BaseFilters />
      <EquipmentsFilters />
      <UserFilters />
      <DateFilters />
    </FlexCol>
  );
}

function BaseFilters() {
  const { filters } = useFiltersContext();
  const { baseData } = useListFilters({ collections: filters.collections, departments: filters.departments });
  const dispatch = useAppDispatch();

  const dependenciesStatus = useAppSelector((state) => selectGetStatus(state, dependenciesListName));

  // Зависимости (Collection/Department/Sector/Process) грузятся ОДИН РАЗ целиком, без
  // серверной фильтрации по текущим выбранным значениям. Иначе после серверной фильтрации,
  // например, по выбранной Коллекции, в стор попадают только строки с этой Коллекцией — и при
  // клиентском пересчёте доступных опций (filterDependenciesOptimized) все остальные Коллекции
  // "исчезают", хотя должны оставаться видимыми (фильтр не должен ограничивать сам себя).
  // Загрузка один раз вместо перезапроса на каждое изменение фильтра также убирает сетевую
  // задержку при выборе фильтров.
  useEffect(() => {
    if (dependenciesStatus !== "IDLE") return;

    dispatch(
      getItems({
        listName: dependenciesListName,
        // В справочнике Dependencies ~8500+ строк (все комбинации Collection/Department/Sector/Process).
        // Лимит 4999 был унаследован от старого лимита SharePoint и обрезал данные — берём с запасом.
        rowLimit: 20000,
        sortOrder: { name: "SortDepend", asc: true },
      }),
    );
  }, [dependenciesStatus, dispatch]);

  useEffect(() => {
    if (filters.collections.length > 0) {
      dispatch(
        getItems({
          listName: linesListName,
          filters: { Collection: filters.collections.map((c) => c.id) },
          rowLimit: 4999,
        }),
      );
    }
  }, [filters.collections]);

  return (
    <>
      {Object.keys(baseData).map((key) => {
        const keyItem = key as Keys | RestKeys;

        return (
          <FilterItem
            key={keyItem}
            keyItem={keyItem}
            title={baseData[keyItem].title}
            data={baseData[keyItem].data}
            filters={filters}
          />
        );
      })}
    </>
  );
}

function EquipmentsFilters() {
  const { filters } = useFiltersContext();

  return (
    <>
      <FilterEquipmentsItem
        title={"Название оборудования"}
        data={filters["titleEquipment"]}
        keyItem={"titleEquipment"}
      />
      <FilterEquipmentsItem
        title={"Производитель оборудования"}
        data={filters["manufacturer"]}
        keyItem={"manufacturer"}
      />
      <FilterEquipmentsItem title={"Модель оборудования"} data={filters["model"]} keyItem={"model"} />
      <FilterEquipmentsItem title={"Тип оборудования"} data={filters["typeEquipment"]} keyItem={"typeEquipment"} />
      <FilterEquipmentsItem title={"SAP № технического места"} data={filters["sap"]} keyItem={"sap"} />
    </>
  );
}

function DateFilters() {
  const { filters } = useFiltersContext();

  return <FilterDateItem title={"Пересмотр документа"} data={filters.dateForRevision} keyItem={"dateForRevision"} />;
}

function UserFilters() {
  const { filters } = useFiltersContext();

  return (
    <>
      <FilterUserItem
        title={"Ответственный за пересмотр"}
        data={filters.responsibleForRevision}
        keyItem={"responsibleForRevision"}
      />
      <FilterUserItem title={"Создатель"} data={filters.created} keyItem={"created"} />
    </>
  );
}

export default DocumentFilters;
